<template>
  <div>
    <div v-if="loading" class="dot-opacity-loader">
      <span></span>
      <span></span>
      <span></span>
    </div>
    <div>
      <router-link
        class="nav-link col-sm-3 pt-4 p-0"
        :to="{ name: downtimeCategorization, params: { deviceId: deviceId } }"
      >
        <b-button variant="primary" size="md" class="btn-block">
          <i class="mdi mdi-library-books text-warning mr-2"></i>
          {{ $t('sidebar.titles.categorization') }}
        </b-button>
      </router-link>
      <div v-if="categoriesData && categoriesData.length">
        <apexchart
          type="bar"
          width="100%"
          :options="chartOptions"
          :series="seriesData"
          :height="chartHeight"
        ></apexchart>
        <div class="text-center">
          <div
            v-for="(item, i) in legends"
            :key="i"
            class="d-inline-flex"
            :class="[i == 1 ? 'mx-3' : '']"
          >
            <div
              style="width: 12px; height: 12px"
              class="m-1"
              :style="'background-color:' + item"
            ></div>
            <span class="text-muted textSize">{{ category(item) }}</span>
          </div>
        </div>
      </div>
      <div v-else class="text-center">
        <h4 class="p-3">{{ $t('dashboards.noData') }}</h4>
      </div>
    </div>
  </div>
</template>

<script>
import downtimeCategorization from '@/api/boxinfo/downtimeCategorization.gql';
import workCenterStatesStat from '@/api/boxinfo/workCenterStatesStat.gql';

export default {
  name: 'DowntimeCategorizationChart',
  components: {
    apexchart: () => import('vue-apexcharts'),
  },
  props: {
    workCenterId: String,
    period: Object,
    deviceId: String,
  },
  apollo: {
    categorizationData: {
      query: downtimeCategorization,
      variables() {
        return {
          workCenterId: this.workCenterId,
          period: this.period,
        };
      },
      pollInterval: 300000,
      update(data) {
        const queryData = data.koeeBoxQuery;
        if (queryData && queryData.efficiencyLossesStat && queryData.efficiencyLossesStat.length) {
          return queryData.efficiencyLossesStat.sort((a, b) => (b.duration > a.duration ? 1 : -1));
        } else {
          return [];
        }
      },
      skip() {
        return !this.workCenterId && !this.period;
      },
      error(e) {
        this.$store.commit('SET_ERROR', e.message);
      },
      fetchPolicy: 'network-only',
    },
    statesStat: {
      query() {
        return workCenterStatesStat;
      },
      variables() {
        return {
          workCenterId: this.workCenterId,
          period: this.period,
        };
      },
      pollInterval: 300000,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        if (queryData && queryData.workCenterStatesStat) {
          return queryData.workCenterStatesStat;
        } else {
          return [];
        }
      },
      skip() {
        return !this.workCenterId && !this.period;
      },

      fetchPolicy: 'network-only',
    },
  },
  data() {
    return {
      downtimeCategorization: 'EventsPageDevice',
      colorPlan: '#0090e7',
      colorUnplan: '#fc424a',
      colorAll: '#6c757d',
      colorShort: '#cfcfcf',
      categorizationData: {},
      downtimeType: 0,
      shortCategoryId: 'short',
    };
  },
  computed: {
    legends() {
      return Array.from(new Set(this.colors));
    },
    stateStatDuration() {
      let duration = 0;
      this.statesStat?.forEach((item) => {
        duration += item.duration;
      });
      return duration * 60;
    },
    categorizationDataDuration() {
      let duration = 0;
      Array.from(this.categorizationData).forEach((item) => {
        duration += item.duration;
      });
      return duration;
    },
    differenceDuration() {
      return this.stateStatDuration - this.categorizationDataDuration;
    },
    categorizationDataWidthShortDuration() {
      let catData = Array.from(this.categorizationData) || [];
      if (this.differenceDuration > 0) {
        let shortDuration = {
          categoryId: this.shortCategoryId,
          duration: this.differenceDuration,
          categoryName: this.shortDurationName,
        };
        catData.push(shortDuration);
      }
      return catData;
    },
    shortDurationName() {
      return this.$t('boxInfo.shortDurationEvents');
    },
    colors() {
      const colors = [];
      this.categorizationDataWidthShortDuration.forEach((element) => {
        if (element.categoryId && element.categoryId.startsWith('AP')) {
          colors.push(this.colorPlan);
        } else if (element.categoryId && element.categoryId.startsWith('AN')) {
          colors.push(this.colorUnplan);
        } else if (element.categoryId && element.categoryId == this.shortCategoryId) {
          colors.push(this.colorShort);
        } else {
          colors.push(this.colorAll);
        }
      });
      return colors;
    },
    chartHeight() {
      switch (this.categoriesData.length) {
        case 0:
          return 0;
        case 1:
          return 124;
        case 2:
          return 191;
        case 3:
          return 253;
        case 4:
          return 325;
        default:
          return 400;
      }
    },
    loading() {
      return this.$apollo.queries.categorizationData.loading;
    },
    categoriesData() {
      if (
        this.categorizationDataWidthShortDuration &&
        this.categorizationDataWidthShortDuration.length
      ) {
        return this.categorizationDataWidthShortDuration.map((i) =>
          i.categoryName ? i.categoryName : this.$t('box.categorization.notСategorized'),
        );
      }
      return {};
    },
    seriesData() {
      return [
        {
          data: this.categorizationDataWidthShortDuration.map((i) => i.duration / 3600),
        },
      ];
    },
    chartOptions() {
      const vm = this;
      return {
        chart: {
          type: 'bar',
          height: this.chartHeight,
          animations: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            barHeight: '90%',
            distributed: true,
            horizontal: true,
            dataLabels: {
              position: 'bottom',
            },
          },
        },
        colors: this.colors,
        dataLabels: {
          enabled: true,
          textAnchor: 'start',
          offsetX: -30,
          style: {
            colors: ['#ffffff'],
            fontWeight: '400',
            fontSize: '14px',
          },
          formatter: function (val, opt) {
            return opt.w.globals.labels[opt.dataPointIndex] + ': ' + vm.timeFormatter(val);
          },
        },
        stroke: {
          curve: 'straight',
          colors: ['#ffffff'],
        },
        xaxis: {
          type: 'category',
          labels: {
            show: false,
            style: {
              colors: ['#6c7293'],
              fontSize: '12px',
            },
          },
          tickAmount: 'dataPoints',
          categories: this.categoriesData,
          crosshairs: {
            show: true,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          show: false,
          showAlways: false,
        },
        grid: {
          padding: {
            left: -20,
          },
          show: false,
        },
        legend: {
          show: false,
        },
        tooltip: {
          enabled: false,
          useSeriesColors: false,
        },
      };
    },
  },
  methods: {
    timeFormatter(value) {
      const day = Math.floor(value / 24);
      const hour = Math.floor(value % 24);
      const minute = Math.floor((value * 60) % 60);
      const days = day >= 1 ? day + this.$t('dashboards.days') : '';
      const hours = hour >= 1 ? hour + this.$t('dashboards.hours') : '';
      const minutes = minute >= 1 ? minute + this.$t('dashboards.minutes') : '';
      const result =
        days === '' && hours === '' && minutes === '' ? '0' : `${days}${hours}${minutes}`;
      return result;
    },
    category(color) {
      return color == this.colorPlan
        ? this.$t('box.categorization.planned')
        : color == this.colorUnplan
        ? this.$t('box.categorization.notPlanned')
        : color == this.colorShort
        ? this.shortDurationName
        : this.$t('box.categorization.notСategorized');
    },
  },
};
</script>
<style>
.textSize {
  font-size: 12px;
}
</style>
